if (!customElements.get('variant-selects')) {
  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
      }

      get selectedOptionValues() {
        return this.optionControls
          .map((control) => {
            if (control.tagName === 'SELECT') {
              const option = control.selectedOptions[0];
              return option?.dataset.optionValueId;
            }
            const input = control.querySelector('input:checked');
            return input?.dataset.optionValueId;
          })
          .filter(Boolean);
      }

      getInputForEventTarget(target) {
        return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
      }

      get optionControls() {
        return Array.from(this.querySelectorAll('[data-option-index]'));
      }

      getOptionControlByRole(role) {
        return this.optionControls.find((control) => (control.dataset.optionRole || 'other') === role);
      }

      get variantData() {
        if (this._variantData) return this._variantData;
        try {
          const rawVariants = this.rawVariantData;
          const optionCount = this.optionControls.length;
          this._variantData = rawVariants.map((variant) => ({
            id: variant.id,
            available: variant.available,
            featured_media: variant.featured_media || null,
            options: [variant.option1, variant.option2, variant.option3].slice(0, optionCount),
          }));
        } catch (error) {
          console.error(error);
          this._variantData = [];
        }
        return this._variantData;
      }

      get rawVariantData() {
        if (this._rawVariantData) return this._rawVariantData;
        const variantsJson = this.querySelector('[data-variants]');
        if (!variantsJson) return [];
        try {
          this._rawVariantData = JSON.parse(variantsJson.textContent);
        } catch (error) {
          console.error(error);
          this._rawVariantData = [];
        }
        return this._rawVariantData;
      }

      connectedCallback() {
        this.applyNoPreselectIfNeeded();
        this.updateOptionStates();
        this.updateSizeAvailabilityByAge();
        const selectedOptions = this.getSelectedOptions();
        const isComplete = this.isSelectionComplete(selectedOptions);
        const matchingVariant = isComplete ? this.findMatchingVariant(selectedOptions) : null;
        this.updateFormState({ isComplete, matchingVariant });

        this.addEventListener('change', (event) => {
          const target = this.getInputForEventTarget(event.target);
          this.updateSelectedSwatchValue(event);
          this.handleColorPrioritySelection(target);
          this.handleColorMediaSelection(target);
          this.updateOptionStates();
          this.updateSizeAvailabilityByAge();

          const selectedOptions = this.getSelectedOptions();
          const isComplete = this.isSelectionComplete(selectedOptions);
          const matchingVariant = isComplete ? this.findMatchingVariant(selectedOptions) : null;

          if (!isComplete || !matchingVariant) {
            this.updateFormState({
              isComplete,
              matchingVariant,
            });
            return;
          }

          this.updateFormState({
            isComplete,
            matchingVariant,
          });

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
            data: {
              event,
              target,
              selectedOptionValues: this.selectedOptionValues,
            },
          });
        });
      }

      updateSelectedSwatchValue({ target }) {
        const { value, tagName } = target;

        if (tagName === 'SELECT' && target.selectedOptions.length) {
          const currentSelected = Array.from(target.options).find((option) => option.getAttribute('selected'));
          if (currentSelected) currentSelected.removeAttribute('selected');
          target.selectedOptions[0].setAttribute('selected', 'selected');

          const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
          const selectedDropdownSwatchValue = target
            .closest('.product-form__input')
            .querySelector('[data-selected-value] > .swatch');
          if (!selectedDropdownSwatchValue) return;
          if (swatchValue) {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
            selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
          } else {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
            selectedDropdownSwatchValue.classList.add('swatch--unavailable');
          }

          selectedDropdownSwatchValue.style.setProperty(
            '--swatch-focal-point',
            target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
          );
        } else if (tagName === 'INPUT' && target.type === 'radio') {
          const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
          if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
        }
      }

      getSelectedOptions() {
        const selectedOptions = [];
        this.optionControls.forEach((control) => {
          const index = Number(control.dataset.optionIndex);
          let value = null;
          if (control.tagName === 'SELECT') {
            if (control.selectedIndex !== -1) {
              value = control.selectedOptions[0]?.value || null;
            }
          } else {
            const input = control.querySelector('input:checked');
            value = input?.value || null;
          }
          selectedOptions[index] = value;
        });
        return selectedOptions;
      }

      isSelectionComplete(selectedOptions) {
        const optionIndexes = this.optionControls.map((control) => Number(control.dataset.optionIndex));
        return optionIndexes.every((index) => selectedOptions[index]);
      }

      findMatchingVariant(selectedOptions) {
        return this.variantData.find((variant) => {
          return selectedOptions.every((value, index) => value && variant.options[index] === value);
        });
      }

      variantMatchesSelection(variant, selectedOptions, ignoreIndex) {
        return selectedOptions.every((value, index) => {
          if (index === ignoreIndex) return true;
          if (!value) return true;
          return variant.options[index] === value;
        });
      }

      hasMatchingVariant(index, value, selectedOptions, availableOnly = false) {
        return this.variantData.some((variant) => {
          if (availableOnly && !variant.available) return false;
          if (variant.options[index] !== value) return false;
          return this.variantMatchesSelection(variant, selectedOptions, index);
        });
      }

      hasAnyAvailableVariant(index, value) {
        return this.variantData.some((variant) => variant.available && variant.options[index] === value);
      }

      updateOptionStates() {
        const selectedOptions = this.getSelectedOptions();
        this.optionControls.forEach((control) => {
          const index = Number(control.dataset.optionIndex);
          const role = control.dataset.optionRole || 'other';

          if (control.tagName === 'SELECT') {
            Array.from(control.options).forEach((option) => {
              const value = option.value;
              const hasAvailableMatch =
                role === 'color'
                  ? this.hasAnyAvailableVariant(index, value)
                  : this.hasMatchingVariant(index, value, selectedOptions, true);
              const shouldHide = role === 'color' && !this.hasAnyAvailableVariant(index, value);

              option.hidden = shouldHide;
              option.disabled = shouldHide || !hasAvailableMatch;
            });
          } else {
            Array.from(control.querySelectorAll('input[type="radio"]')).forEach((input) => {
              const value = input.value;
              const hasAvailableMatch =
                role === 'color'
                  ? this.hasAnyAvailableVariant(index, value)
                  : this.hasMatchingVariant(index, value, selectedOptions, true);
              const shouldHide = role === 'color' && !this.hasAnyAvailableVariant(index, value);

              input.hidden = shouldHide;
              input.disabled = shouldHide || !hasAvailableMatch;
              input.classList.toggle('disabled', input.disabled);

              const label = this.querySelector(`label[for="${input.id}"]`);
              if (label) {
                label.classList.toggle('hidden', shouldHide);
              }
            });
          }
        });
      }

      updateSizeAvailabilityByAge() {
        const ageControl = this.getOptionControlByRole('age');
        const sizeControl = this.getOptionControlByRole('size');
        if (!ageControl || !sizeControl) return;

        const ageIndex = Number(ageControl.dataset.optionIndex);
        const sizeIndex = Number(sizeControl.dataset.optionIndex);
        if (Number.isNaN(ageIndex) || Number.isNaN(sizeIndex)) return;

        const selectedOptions = this.getSelectedOptions();
        const selectedAge = selectedOptions[ageIndex];

        if (!selectedAge) {
          this.setSizeAvailabilityByAge(sizeControl, null);
          return;
        }

        const availableSizes = this.getAvailableSizesByAge(ageIndex, sizeIndex, selectedAge);
        this.setSizeAvailabilityByAge(sizeControl, availableSizes);
      }

      getAvailableSizesByAge(ageIndex, sizeIndex, selectedAge) {
        const availableSizes = new Set();
        this.variantData.forEach((variant) => {
          if (variant.options[ageIndex] === selectedAge && variant.options[sizeIndex]) {
            availableSizes.add(variant.options[sizeIndex]);
          }
        });
        return availableSizes;
      }

      setSizeAvailabilityByAge(sizeControl, availableSizes) {
        if (sizeControl.tagName === 'SELECT') {
          Array.from(sizeControl.options).forEach((option) => {
            if (!option.value) {
              this.toggleSizeUnavailableClass(option, false);
              return;
            }
            const isAvailable = availableSizes ? availableSizes.has(option.value) : true;
            this.toggleSizeUnavailableClass(option, !isAvailable);
          });
          return;
        }

        Array.from(sizeControl.querySelectorAll('input[type="radio"]')).forEach((input) => {
          const isAvailable = availableSizes ? availableSizes.has(input.value) : true;
          this.applySizeUnavailableToInput(input, !isAvailable);
        });
      }

      applySizeUnavailableToInput(input, isUnavailable) {
        this.toggleSizeUnavailableClass(input, isUnavailable);
        const label = this.querySelector(`label[for="${input.id}"]`);
        if (label) {
          this.toggleSizeUnavailableClass(label, isUnavailable);
        }
      }

      toggleSizeUnavailableClass(element, isUnavailable) {
        element.classList.toggle('size--unavailable-by-age', isUnavailable);
      }

      handleColorPrioritySelection(target) {
        const colorControl = this.getOptionControlByRole('color');
        if (!colorControl || !this.isOptionInputFromControl(target, colorControl)) return;

        const sizeControl = this.getOptionControlByRole('size');
        const ageControl = this.getOptionControlByRole('age');
        if (sizeControl) {
          this.resetOptionControlSelection(sizeControl);
        }
        if (ageControl) {
          this.resetOptionControlSelection(ageControl);
        }
      }

      isOptionInputFromControl(target, control) {
        if (control.tagName === 'SELECT') {
          return target === control || target.closest('select') === control;
        }
        return control.contains(target);
      }

      handleColorMediaSelection(target) {
        const colorControl = this.getOptionControlByRole('color');
        if (!colorControl || !this.isOptionInputFromControl(target, colorControl)) return;

        const optionIndex = Number(colorControl.dataset.optionIndex);
        if (Number.isNaN(optionIndex)) return;

        const selectedValue = this.getSelectedValueFromControl(colorControl);
        if (!selectedValue) return;

        const variant = this.getVariantByOptionValue(optionIndex, selectedValue);
        if (!variant || !variant.featured_media) return;

        this.updateMediaGallery(variant);
      }

      getSelectedValueFromControl(control) {
        if (control.tagName === 'SELECT') {
          return control.value || null;
        }
        const input = control.querySelector('input:checked');
        return input?.value || null;
      }

      getVariantByOptionValue(optionIndex, optionValue) {
        return this.variantData.find((variant) => variant.options[optionIndex] === optionValue) || null;
      }

      updateMediaGallery(variant) {
        const productInfo = this.closest('product-info');
        const mediaGallery = productInfo?.querySelector('media-gallery');
        if (mediaGallery?.setActiveMedia) {
          mediaGallery.setActiveMedia(variant);
        }
      }

      resetOptionControlSelection(control) {
        if (control.tagName === 'SELECT') {
          Array.from(control.options).forEach((option) => option.removeAttribute('selected'));
          control.selectedIndex = -1;
        } else {
          control.querySelectorAll('input:checked').forEach((input) => {
            input.checked = false;
          });
        }

        const selectedValue = control.closest('.product-form__input')?.querySelector('[data-selected-swatch-value]');
        if (selectedValue) selectedValue.textContent = '';
      }

      applyNoPreselectIfNeeded() {
        if (this.dataset.noPreselect !== 'true' || this.dataset.gpoProduct === 'true') return;
        const productInfo = this.closest('product-info');
        if (productInfo?.dataset.noPreselectInitialized === 'true') return;

        this.optionControls.forEach((control) => {
          if (control.tagName === 'SELECT') {
            Array.from(control.options).forEach((option) => option.removeAttribute('selected'));
            control.selectedIndex = -1;
          } else {
            control.querySelectorAll('input:checked').forEach((input) => {
              input.checked = false;
            });
          }

          const selectedValue = control.closest('.product-form__input')?.querySelector('[data-selected-swatch-value]');
          if (selectedValue) selectedValue.textContent = '';
        });

        if (productInfo) {
          productInfo.dataset.noPreselectInitialized = 'true';
        }
      }

      updateFormState({ isComplete = false, matchingVariant = null } = {}) {
        const productInfo = this.closest('product-info');
        if (!productInfo) return;

        const sectionId = productInfo.dataset.section;
        const forms = productInfo.querySelectorAll('form[is="product-form"]');
        const isAvailable = matchingVariant?.available === true;
        const shouldDisable = !isComplete || !matchingVariant || !isAvailable;
        const hintText = !isComplete || !matchingVariant ? FoxTheme.variantStrings.unavailable : FoxTheme.variantStrings.soldOut;

        forms.forEach((form) => {
          const idInput = form.querySelector('input[name="id"]');
          const addButton = form.querySelector('[name="add"]');
          const addButtonText = addButton?.querySelector('span');

          if (idInput) idInput.value = matchingVariant?.id ?? '';

          if (addButton) {
            if (addButtonText && !addButtonText.dataset.defaultText) {
              addButtonText.dataset.defaultText = addButtonText.textContent?.trim() || '';
            }
            if (shouldDisable) {
              addButton.setAttribute('disabled', 'disabled');
              if (addButtonText) addButtonText.textContent = hintText;
            } else {
              addButton.removeAttribute('disabled');
              if (addButtonText) addButtonText.textContent = addButtonText.dataset.defaultText;
            }
          }
        });

        const stickyButtons = document.querySelectorAll(
          `sticky-atc-bar-minimalist[data-section="${sectionId}"] [name="add"], sticky-atc-bar[data-section="${sectionId}"] [name="add"]`
        );
        stickyButtons.forEach((button) => {
          const buttonText = button.querySelector('span');
          if (buttonText && !buttonText.dataset.defaultText) {
            buttonText.dataset.defaultText = buttonText.textContent?.trim() || '';
          }
          if (shouldDisable) {
            button.setAttribute('disabled', 'disabled');
            if (buttonText) buttonText.textContent = hintText;
          } else {
            button.removeAttribute('disabled');
            if (buttonText) buttonText.textContent = buttonText.dataset.defaultText;
          }
        });
      }
    }
  );
}
